import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../common/types';
import { SMLButton } from '../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../routing/sml/hooks';
import { SMLRoutes } from '../../routing/sml/SMLRoutes';
import { useIsNativeEmbed } from '../music24/navigation/hooks/useIsNativeEmbed';
import { detectMobileOs, SPOTIFY_FOR_ARTISTS_CONNECT_PATH } from './mobileAppLinks';
import { usePhoneConnectWatch } from './usePhoneConnectWatch';
import { MobileAppHandoff } from './MobileAppHandoff';
import { PhoneAppHandoff } from './PhoneAppHandoff';
import { HandoffSteps } from './HandoffSteps';
import { PhoneConnectStatusRow } from './PhoneConnectStatusRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole handoff; each part uses its own slice of it */
import styles from './MobileApps.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PhoneConnectHandoffProps {
  accountId: number;
  integrationType: string;
  serviceName: string;
  onClose: () => void;
  /** The app path the phone opens. Default: the Spotify for Artists connect in Catalog. */
  next?: string;
  /** A note under the lead, for example the Google hint of Ditto Music (design your-intelligence k2). */
  hint?: React.ReactNode;
  /** The login happens on the page of the service (Ditto Music). Step 3 says so. */
  logsInOnServicePage?: boolean;
}

/**
 * The connect sheet's answer when a browser cannot finish the sign-in (design
 * music24-mobile-apps 1a, 1d/d1, 2a, 2c). Spotify for Artists and Ditto Music take this path. The
 * handoff link opens the connect of the service in Catalog of the app (design your-intelligence k2).
 */
const PhoneConnectHandoffComponent: React.FC<PhoneConnectHandoffProps> = ({
  accountId,
  integrationType,
  serviceName,
  onClose,
  next = SPOTIFY_FOR_ARTISTS_CONNECT_PATH,
  hint,
  logsInOnServicePage = false,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const isNativeEmbed = useIsNativeEmbed();
  const os = useMemo(() => (isNativeEmbed ? undefined : detectMobileOs(navigator.userAgent)), [isNativeEmbed]);
  const { status, artistName, checkAgain } = usePhoneConnectWatch({ accountId, integrationType });

  const goToStreaming = useCallback(() => {
    onClose();
    router.push(SMLRoutes.streaming);
  }, [onClose, router]);

  const lead = intl.formatMessage(
    {
      id: 'app.sml.mobileApps.connect.lead',
      defaultMessage: '{service} allows this sign-in only inside the MUSIC24 app. It takes about a minute.',
    },
    { service: serviceName }
  );

  if (status === 'connected') {
    return (
      <div className={styles.handoff}>
        <p className={styles.lead}>
          {intl.formatMessage(
            {
              id: 'app.sml.mobileApps.connect.doneStreaming',
              defaultMessage: 'Connected for {artist}. Streaming fills in within a few minutes.',
            },
            { artist: artistName ?? '' }
          )}
        </p>
        <HandoffSteps serviceName={serviceName} logsInOnServicePage={logsInOnServicePage} isDone />
        <div className={styles.actions}>
          <SMLButton color="primary" onClick={goToStreaming}>
            {intl.formatMessage({ id: 'app.sml.mobileApps.connect.goToStreaming', defaultMessage: 'Go to Streaming' })}
          </SMLButton>
        </div>
      </div>
    );
  }

  if (os !== undefined) {
    return (
      <>
        <PhoneAppHandoff os={os} lead={lead} next={next} />
        {hint}
      </>
    );
  }

  return (
    <MobileAppHandoff
      lead={lead}
      next={next}
      footer={<PhoneConnectStatusRow status={status} onCheckAgain={checkAgain} />}
    >
      <HandoffSteps serviceName={serviceName} logsInOnServicePage={logsInOnServicePage} />
      {hint}
    </MobileAppHandoff>
  );
};

export const PhoneConnectHandoff = TypedMemo(PhoneConnectHandoffComponent);
